import React, { useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { animateScroll } from 'react-scroll';
import { useSearchParams } from 'react-router-dom';

import MediaIndexFilters from '@buyside/components/presentation/MediaIndexPage/mediaIndexFilters';
import MediaIndexItem from '@buyside/components/presentation/MediaIndexPage/mediaIndexItem';
import MediaIndexItemLoading from '@buyside/components/presentation/MediaIndexPage/mediaIndexItemLoading';
import { MediaTypes } from '@buyside/components/presentation/MediaShowPage/mediaShowPage';

import useInfiniteScroll from '@buyside/util/hooks/useInfiniteScroll';

import { MediaIndexListQuery, useMediaIndexListQuery } from '@interfaces/graphql-typings';

import styles from './mediaIndexPageStyles.scss';

const MediaIndexPage: React.FC<{ mediaType: MediaTypes }> = ({ mediaType }) => {
  const [searchParams] = useSearchParams();

  const { data, fetchMore, loading } = useMediaIndexListQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      searchParams: searchParams.toString(),
      indexType: `${mediaType.replace(' ', '')}sIndex`,
      type: mediaType.replace(' ', '')
    }
  });

  const { mostPopularMediaHashtags, mediaIndexList, indexListResultCount } = data || {};
  const { pageInfo, edges } = mediaIndexList || {};

  const skeletonCount = Math.min((indexListResultCount || 20) - (edges?.length || 0), 20);

  useInfiniteScroll(
    Boolean(pageInfo?.hasNextPage && !loading),
    (): Promise<ApolloQueryResult<MediaIndexListQuery>> =>
      fetchMore({
        variables: {
          cursor: pageInfo?.endCursor
        }
      })
  );

  useEffect(() => animateScroll.scrollToTop, [searchParams]);

  return (
    <>
      <h3>{`${mediaType}s`}</h3>
      <MediaIndexFilters
        indexListResultCount={indexListResultCount || 0}
        loading={loading}
        mediaType={mediaType}
        mostPopularMediaHashtags={mostPopularMediaHashtags || []}
      />
      <div className={styles.mediaIndexList}>
        {edges?.map(edge =>
          edge?.node ? (
            <MediaIndexItem {...edge.node} key={`media-index-item-${edge.node.id}`} />
          ) : null
        )}
        {indexListResultCount === 0 ? <p>No results</p> : null}
        {loading
          ? [...Array(skeletonCount).keys()].map(key => (
              <MediaIndexItemLoading key={`media-index-item-${key}`} />
            ))
          : null}
      </div>
    </>
  );
};

export default MediaIndexPage;
